import { Catalog, FilterCatalog } from '../../../../common/types/common';
import { EMPTY_CATALOG, EMPTY_FILTER_CATALOG } from '../../../constants/GlobalConstants';
import { DocumentsGroupsDTO, VehicleSummaryDTO, CreditConditionSummaryDTO } from '../types/epics';
import {
    AttachmentFileState,
    BankTarget,
    LoanApplicationAttachmentsState,
    LoanGroupedAttachmentsState
} from '../types/types';
import { FieldSummary } from '../../../types/mx/epics';
import {
    VehicleDataViewFieldErrorState, PreviewLoanApplicationVehicleDataFieldErrors, PreviewFieldError,
    CreditConditionViewFieldErrorState, PreviewLoanApplicationCreditConditionsDataFieldErrors, PreviewFieldErrorGeneric
} from '../types/reducers';
import { CALCULATOR } from '../../../constants/LabelConstants';
import { ERROR_REQUIRED, ERROR_MIN_LENGTH, ERROR_MAX_LENGTH } from '../../../constants/ValidateConstant';
import { getSafe } from '../../../../common/utils/GeneralUtils';
import {CLOSING_BANK_NAMES} from '../constants/LabelConstants';

export const findElementInMenu = (menu = [], itemMenu, roles = []) => {
    return !!getSafe(() => menu.find(menu => menu.id === itemMenu)
        .allowedRoles.find(role => roles.includes(role)));
};

export const filterCatalogToCatalogMap = (item: FilterCatalog): Catalog => ({
    id: item.id,
    value: `${item.name} (${item.count})`,
    payload: ''
});

export const findOnFilterCatalog = (catalogs: FilterCatalog[], id: string) =>
    id ? catalogs.find(item => item.id === id) : EMPTY_FILTER_CATALOG;

export const findOnCatalog = (catalogs: Catalog[], id: string): Catalog =>
    id ? catalogs.find(item => item.id === id) : EMPTY_CATALOG;

export const hasErrorField = (fieldValue: string, fieldError: string): boolean =>
    !fieldValue || fieldValue === '' || fieldError !== null;

export const verifyFieldsForError = (fieldErrors: any): boolean => {
    return fieldErrors && Object.keys(fieldErrors).reduce((result: boolean, field: string) => {
        return result || fieldErrors[field].hasError;
    }, false);
};

export const verifyFieldsErrorForCalculator = (fieldErrors: any): boolean => {
    return fieldErrors && Object.keys(fieldErrors).reduce((result: boolean, field: string) => {
        return result || fieldErrors[field];
    }, false);
};

export const mapLoanApplicationDocuments = (groupDocs: DocumentsGroupsDTO[]): LoanGroupedAttachmentsState[] => {
    return groupDocs.map(document => ({
        classification: document.classification,
        classificationId: document.classificationId,
        documentGroups: document.documentGroups.map(documentGroup => ({
            id: documentGroup.documentType.id,
            autogenerated: documentGroup.documentType.autogenerated,
            info: documentGroup.documentType.info,
            label: documentGroup.documentType.label,
            versionId: documentGroup.lastVersion.versionId,
            versionName: documentGroup.lastVersion.name,
            documentGroups: documentGroup.lastVersion.documents.map(item => ({
                ...item,
                sentToBank: !!item.sentToCreditoReal
            })) as AttachmentFileState[],
            versions: documentGroup.otherVersions.map(item => ({
                name: item.name,
                versionId: item.versionId,
                documentGroups: item.documents.map(item => ({
                    ...item,
                    fileName: fixFileName(item.fileName, item.mimeType),
                    sentToBank: !!item.sentToCreditoReal
                })) as AttachmentFileState[],
                openTree: false
            }))
        } as LoanApplicationAttachmentsState))
    } as LoanGroupedAttachmentsState));
};

const fixFileName = (fileName: string, mimeType: string): string => {
    const allExtensions = ['.jpeg','.jpg', '.png', '.pdf'];
    const hasSomeExtension = allExtensions.some(x => fileName.endsWith(x));
    if (!hasSomeExtension) {
        let newExtension: string;
        switch (mimeType) {
            case 'image/jpeg': newExtension = '.jpeg'; break;
            case 'image/png': newExtension = '.png'; break;
            case 'image/pdf': newExtension = '.pdf'; break;
            default: newExtension = '.jpg'; break;
        }
        return fileName + newExtension
    }
    return fileName
}

export const getTargetName = (target: string): string => {
    switch (target) {
        case BankTarget.CREDITO_REAL:
            return 'Crédito Real';
        case BankTarget.TURBO_CASH:
            return 'Turbo Cash';
        default:
            return '';
    }
};

export const verifyVehicleDataForLAMenuErrors =
    (vehicleDataFS: FieldSummary<VehicleSummaryDTO, VehicleDataViewFieldErrorState>):
        PreviewLoanApplicationVehicleDataFieldErrors => {
        const fe = vehicleDataFS.fieldErrors;
        const data = vehicleDataFS.data;

        const hasYearError: boolean =
            hasErrorField(data.year > 0 ? data.year.toString() : '', fe.yearFieldError);
        const brandID = data.brand ? data.brand.id : '';
        const modelID = data.model ? data.model.id : '';
        const versionID = data.version ? data.version.id : '';
        const hasBrandError: boolean = hasErrorField(brandID, fe.brandFieldError);
        const hasModelError: boolean = hasErrorField(modelID, fe.modelFieldError);
        const hasVersionError: boolean = hasErrorField(versionID, fe.versionFieldError);

        return {
            hasYearError: new PreviewFieldError(CALCULATOR.YEAR, hasYearError),
            hasBrandError: new PreviewFieldError(CALCULATOR.BRAND, hasBrandError),
            hasModelError: new PreviewFieldError(CALCULATOR.MODEL, hasModelError),
            hasVersionError: new PreviewFieldError(CALCULATOR.VERSION, hasVersionError)
        };
    };

export const verifyCreditDataForLAMenuErrors =
    (creditDataFS: FieldSummary<CreditConditionSummaryDTO, CreditConditionViewFieldErrorState>):
        PreviewLoanApplicationCreditConditionsDataFieldErrors => {

        const fe = creditDataFS.fieldErrors;
        const data = creditDataFS.data;

        const hasPriceError: boolean = hasErrorField(data.price, fe.vehiclePriceFieldError);
        const hasDownPaymentError: boolean = hasErrorField(data.downPayment, fe.downPaymentFieldError);
        const hasTermError: boolean = hasErrorField(data.term, fe.termSelectedFieldError);

        return {
            hasPriceError: new PreviewFieldError(CALCULATOR.PRICE, hasPriceError),
            hasDownPaymentError: new PreviewFieldError(CALCULATOR.DOWN_PAYMENT, hasDownPaymentError),
            hasTermError: new PreviewFieldError(CALCULATOR.TERMS, hasTermError),
        };
    };

const isEmptyFirstAndFullSecond = (firstField, secondField) => !!!firstField && !!secondField;
const isEmptySecondAndFullFirst = (firstField, secondField) => !!firstField && !!!secondField;
const isEmptyFirstAndSecond = (firstField, secondField) => !!!firstField && !!!secondField;

export const firstValidationWithSecond = (firstField, secondField, defaultError = null) =>
    isEmptyFirstAndFullSecond(firstField, secondField) ? ERROR_REQUIRED :
        isEmptyFirstAndSecond(firstField, secondField) ? null : defaultError;

export const secondValidationWithFirst = (firstField, secondField, maxLengthBothFields, defaultError = null) => {
    const totalLength = getSafe(() => firstField.length + secondField.length);

    if (isEmptySecondAndFullFirst(firstField, secondField)) { return ERROR_REQUIRED; }
    if (getSafe(() => firstField.length) === 1) { return null; }
    if (isEmptyFirstAndFullSecond(firstField, secondField)) { return null; }
    if (!!firstField) {
        if (totalLength < maxLengthBothFields) {
            return ERROR_MIN_LENGTH(maxLengthBothFields - firstField.length);
        }
        if (totalLength > maxLengthBothFields) {
            return ERROR_MAX_LENGTH(maxLengthBothFields - firstField.length);
        }
        return defaultError;
    }
    if (isEmptyFirstAndSecond(firstField, secondField)) { return null; }
    return defaultError;
};

export function checkLAErrorsForMenu<A, T>(fieldErrors: A, labels: A, initialValue: any = {}): T {
    return Object.entries(fieldErrors).reduce((fieldsObject, [fieldName, fieldValue]) => {
        return {
            ...fieldsObject,
            [fieldName]: new PreviewFieldError(
                labels[fieldName],
                initialValue ? fieldValue !== null && fieldValue !== undefined : false
            )
        };
    }, initialValue);
}

export const haveNoErrors = (fieldErrors): boolean =>
    Object.entries(fieldErrors).every(([fieldName, fieldValue]) => fieldValue == null);

export const bankApproved = (coppel, creditas, rapidauto): string  => {
    const isCoppelApproved = coppel?.id ? coppel?.id === 'A' : false;
    const isCreditasApproved = creditas?.id ? creditas?.id === 'A': false;
    const isRapidautoApproved = rapidauto?.id ? rapidauto?.id === 'A': false;
    return isCoppelApproved ? CLOSING_BANK_NAMES.coppel: isCreditasApproved ? CLOSING_BANK_NAMES.creditas : isRapidautoApproved ? CLOSING_BANK_NAMES.rapidauto : ''
}

export const validateShouldChangeClosingBank = (newBankValue, oldBankValue1, oldBankValue2) => newBankValue.id !== 'A' || !(newBankValue.id === 'A' && (oldBankValue1.id === 'A' || oldBankValue2.id === 'A'));
